import { Button, CodeBlock, EmptyState } from '@myog/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, apiFailure } from '../lib/api-client.js';

/**
 * Page API Keys.
 *
 * Le parcours critique est la révélation de la clé (docs/DESIGN.md §5.3) :
 * la valeur brute n'existe que dans cette réponse HTTP. Si l'utilisateur
 * ferme le modal sans copier, elle est définitivement perdue — d'où le
 * bouton de confirmation verrouillé trois secondes.
 */

interface RevealedKey {
  id: string;
  name: string;
  prefix: string;
  type: 'public' | 'secret';
  raw: string;
}

export function ApiKeys() {
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState<RevealedKey | null>(null);

  const keys = useQuery({
    queryKey: ['keys'],
    queryFn: async () => {
      const response = await api.v1.keys.$get();
      if (!response.ok) throw await apiFailure(response, 'Impossible de charger les clés.');
      return response.json();
    },
  });

  const create = useMutation({
    mutationFn: async (input: { name: string; type: 'public' | 'secret' }) => {
      const response = await api.v1.keys.$post({
        json: { ...input, allowedReferers: [] },
      });
      if (!response.ok) throw await apiFailure(response, 'Création impossible.');
      return response.json();
    },
    onSuccess: (data) => {
      setRevealed(data.key);
      void queryClient.invalidateQueries({ queryKey: ['keys'] });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.v1.keys[':id'].$delete({ param: { id } });
      if (!response.ok) throw await apiFailure(response, 'Révocation impossible.');
      return response.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keys'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text">Clés API</h1>
          <p className="mt-1 text-sm text-text-muted">
            Les clés publiques (<code className="font-mono">myog_pk_</code>) s'utilisent dans le
            HTML. Les clés secrètes (<code className="font-mono">myog_sk_</code>) restent côté
            serveur.
          </p>
        </div>
        <Button
          onClick={() => create.mutate({ name: 'Nouvelle clé', type: 'public' })}
          loading={create.isPending}
        >
          Créer une clé
        </Button>
      </header>

      {/*
       * Les échecs sont rendus explicitement.
       *
       * Sans ce bloc, une erreur de chargement retombait sur le même état
       * vide qu'une absence de clé, et une création en échec ne produisait
       * rien du tout : le bouton tournait puis revenait. Deux pannes
       * distinctes, un seul écran, aucun message — impossible à diagnostiquer
       * sans ouvrir la console.
       */}
      {keys.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger"
        >
          {keys.error.message}
        </p>
      ) : null}

      {create.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger"
        >
          {create.error.message}
        </p>
      ) : null}

      {revoke.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger"
        >
          {revoke.error.message}
        </p>
      ) : null}

      {keys.isPending ? (
        <div className="h-40 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
      ) : keys.isError ? null : keys.data && keys.data.keys.length > 0 ? (
        <table className="w-full overflow-hidden rounded-lg border border-border">
          <caption className="sr-only">Vos clés API</caption>
          <thead className="bg-surface">
            <tr className="text-left text-xs uppercase tracking-wide text-text-subtle">
              <th scope="col" className="px-4 py-3 font-medium">
                Nom
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Clé
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Type
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Dernier usage
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {keys.data.keys.map((key) => (
              <tr key={key.id} className="text-sm">
                <td className="px-4 py-3 font-medium text-text">{key.name}</td>
                <td className="px-4 py-3 font-mono text-text-muted">{key.prefix}••••••••</td>
                <td className="px-4 py-3">
                  <span
                    className={
                      key.type === 'secret'
                        ? 'rounded-full bg-warning/10 px-2 py-1 text-xs font-medium text-warning'
                        : 'rounded-full bg-accent-subtle px-2 py-1 text-xs font-medium text-accent'
                    }
                  >
                    {key.type === 'secret' ? 'secrète' : 'publique'}
                  </span>
                </td>
                <td className="px-4 py-3 text-text-muted">
                  {key.lastUsedAt
                    ? new Date(key.lastUsedAt * 1000).toLocaleDateString('fr-FR')
                    : 'jamais'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Révoquer « ${key.name} » ? Les requêtes utilisant cette clé échoueront sous 60 secondes.`,
                        )
                      ) {
                        revoke.mutate(key.id);
                      }
                    }}
                  >
                    Révoquer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="Aucune clé pour l'instant"
          description="Créez votre première clé API pour commencer à générer des images."
        />
      )}

      {revealed ? <RevealModal keyData={revealed} onClose={() => setRevealed(null)} /> : null}
    </div>
  );
}

/**
 * Modal de révélation.
 *
 * Le bouton de fermeture reste désactivé trois secondes : sans ce délai,
 * le réflexe de fermer le modal fait perdre la clé, et l'utilisateur doit
 * en recréer une — friction évitable.
 */
function RevealModal({ keyData, onClose }: { keyData: RevealedKey; onClose: () => void }) {
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (countdown === 0) return;
    const timer = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    function onEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape' && countdown === 0) onClose();
    }
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [countdown, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reveal-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
    >
      <div className="w-full max-w-xl space-y-4 rounded-xl border border-border bg-surface-raised p-6 shadow-lg">
        <h2 id="reveal-title" className="text-lg font-semibold text-text">
          Votre clé « {keyData.name} »
        </h2>

        <p className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          Cette clé ne sera plus jamais affichée. Copiez-la maintenant et stockez-la dans un
          gestionnaire de secrets.
        </p>

        <CodeBlock code={keyData.raw} language={keyData.type === 'secret' ? 'env' : 'html'} />

        <div className="flex justify-end">
          <Button onClick={onClose} disabled={countdown > 0}>
            {countdown > 0 ? `J'ai enregistré ma clé (${countdown})` : "J'ai enregistré ma clé"}
          </Button>
        </div>
      </div>
    </div>
  );
}
